// src/services/mediaTools.js
//
// 🟢 BROWSER MEDIA PREP — the web twin of the phone's pre-upload work.
//
// Before a photo/video is encrypted and uploaded, the phone measures it, builds a
// tiny blurred preview, and normalizes odd formats (CryptoService.encryptImage +
// ChatModal ~20850). The RECEIVER'S UI depends on those side products:
//
//   • width / height          → the bubble reserves the right box before bytes land,
//                               so an incoming photo never "pops" the list
//   • imageThumbnailBase64    → the blurred placeholder the phone paints INSTANTLY
//     videoThumbnailBase64      (and the only thing a backgrounded FCM push carries)
//     batchThumbnails[]
//
// Skip them and a web-sent photo shows up on the phone as a grey box until the full
// ciphertext downloads. So we reproduce all three here, byte-for-byte compatible:
// the thumbnail is a BARE base64 JPEG string (no `data:` prefix — MessageBubble's
// dataUriFromB64 adds it) and is kept under the phone's FCM budget.

// Phone: THUMB_B64_CAP = 1100 chars (~825B) so a 1:1 push never exceeds FCM's limit.
const THUMB_B64_CAP = 1100;
const THUMB_WIDTHS = [50, 36, 24];

// Formats every browser AND both phone platforms can decode. Anything else gets
// transcoded to JPEG (the phone does the same for HEIC).
const WEB_SAFE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024;

export function isImageFile(f) {
  return !!f && (f.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i.test(f.name || ''));
}
export function isVideoFile(f) {
  return !!f && (f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name || ''));
}

export function readFileBytes(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(new Uint8Array(fr.result));
    fr.onerror = () => reject(fr.error || new Error('read failed'));
    fr.readAsArrayBuffer(file);
  });
}

/**
 * Does this look like an iPhone photo the browser cannot read?
 *
 * 🟥 THE MIME IS NOT ENOUGH. A HEIC that has been through AirDrop, a download
 *    folder or another app very often arrives with an EMPTY `type` or a
 *    hopeful `image/jpeg`, so a check on the mime alone misses exactly the
 *    files people actually have. The magic bytes are the only sure test, and
 *    they are what `sniffHeic` reads; this is the cheap pre-filter.
 */
export function looksHeic(file) {
  const t = String(file?.type || '').toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  return /\.(heic|heif)$/i.test(String(file?.name || ''));
}

/**
 * The bytes, definitively.
 *
 * An ISO-BMFF file has `ftyp` at offset 4 and its brand at offset 8; the HEIF
 * brands are `heic`, `heix`, `hevc`, `hevx`, `mif1` and `msf1`.
 */
async function sniffHeic(file) {
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    const str = (a, b) => String.fromCharCode(...head.slice(a, b));
    if (str(4, 8) !== 'ftyp') return false;
    return ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(str(8, 12));
  } catch { return false; }
}

/**
 * A HEIC, as a JPEG the browser can draw.
 *
 * 🟥 NO BROWSER BUT SAFARI CAN DECODE HEIC AT ALL — `createImageBitmap` throws
 *    "The source image could not be decoded" on Chrome and Firefox, which is
 *    why this file used to give up and tell the person to convert the photo
 *    themselves. That is a reasonable answer right up until the photo is the
 *    one thing they are trying to send.
 *
 * 🟢 THE DECODER IS LOADED ONLY WHEN ONE TURNS UP. `heic2any` is a megabyte of
 *    libheif compiled to wasm; a static import would put it in the main bundle
 *    for every visitor who never touches an iPhone photo. A dynamic import
 *    makes it a chunk that is fetched the first time somebody picks one.
 *
 * 🟥 AND SAFARI IS LEFT ALONE. It decodes HEIC natively, so the caller tries
 *    the ordinary path FIRST and only lands here when that has already failed.
 */
async function heicToJpeg(file) {
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  /* 🟥 IT RETURNS AN ARRAY FOR A LIVE PHOTO OR A BURST — one blob per frame.
     Taking the whole array through as a Blob produces "[object Object]" bytes. */
  const blob = Array.isArray(out) ? out[0] : out;
  /* 🟥 ANY TRAILING EXTENSION COMES OFF, NOT ONLY `.heic`. A HEIC that arrives
     named `photo.jpg` — which happens, and is exactly the case the byte sniff
     exists to catch — would otherwise be renamed `photo.jpg.jpg`. */
  const name = String(file.name || 'photo').replace(/\.[^.]{1,5}$/, '') || 'photo';
  return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
}

/**
 * A file every upload path can hand on: the original where the browser can read
 * it, a JPEG where it cannot.
 *
 * 🟢 EXPORTED, because three different surfaces upload images — a chat photo, a
 *    support attachment and a Vibes post — and only the first went anywhere
 *    near this file. A rule with one home is the only way they stay in step.
 */
export async function normalizeImageFile(file) {
  if (!file || !isImageFile(file)) return file;
  /* 🟢 THE CHEAP TEST FIRST, then the bytes. Slicing sixteen bytes off every
     photo somebody picks is not free, and the name or mime settles most of it. */
  if (!looksHeic(file) && !(await sniffHeic(file))) return file;
  try {
    /* Safari can already draw it — converting would cost a second and a
       megabyte of wasm for nothing. */
    if (typeof createImageBitmap === 'function') {
      try {
        const bmp = await createImageBitmap(file);
        bmp.close?.();
        return file;
      } catch { /* every other browser lands here */ }
    }
    return await heicToJpeg(file);
  } catch (e) {
    /* 🟥 A FAILED CONVERSION RETURNS THE ORIGINAL RATHER THAN THROWING. The
       caller then does what it did before this existed — which for the chat
       path is an honest error, and for the others is an upload that at least
       reaches the server. Losing the send outright is the worse outcome. */
    return file;
  }
}

/** Decode to something drawable. createImageBitmap is the fast path; the <img>
 *  fallback keeps Safari < 15 working.
 *  🟢 AND A HEIC IS CONVERTED RATHER THAN REFUSED — see `normalizeImageFile`. */
async function decodeImage(file) {
  const url = URL.createObjectURL(file);
  try {
    if (typeof createImageBitmap === 'function') {
      try {
        const bmp = await createImageBitmap(file);
        return { drawable: bmp, width: bmp.width, height: bmp.height, release: () => bmp.close?.() };
      } catch { /* fall through to <img> */ }
    }
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });
    return { drawable: img, width: img.naturalWidth, height: img.naturalHeight, release: () => {} };
  } finally {
    // the <img> path needs the URL alive until onload fired, which it has by now
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 0);
  }
}

function drawToCanvas(drawable, w, h) {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  const ctx = c.getContext('2d');
  ctx.drawImage(drawable, 0, 0, c.width, c.height);
  return c;
}

function canvasToBytes(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? b.arrayBuffer().then((ab) => resolve(new Uint8Array(ab))) : reject(new Error('encode failed'))),
      mime,
      quality
    );
  });
}

function canvasToBareBase64(canvas, quality) {
  // toDataURL → strip the "data:image/jpeg;base64," prefix. The phone stores and
  // ships the BARE string; adding the prefix here would double it up on the bubble.
  const dataUri = canvas.toDataURL('image/jpeg', quality);
  const comma = dataUri.indexOf(',');
  return comma >= 0 ? dataUri.slice(comma + 1) : '';
}

/** Tiny blurred preview, shrinking until it fits the phone's FCM budget.
 *  Same ladder the app walks: 50px → 36px → 24px at heavy compression. */
export function makeThumbBase64(drawable, srcW, srcH) {
  let best = null;
  for (const w of THUMB_WIDTHS) {
    try {
      const scale = w / Math.max(1, srcW);
      const c = drawToCanvas(drawable, w, Math.max(1, Math.round(srcH * scale)));
      const b64 = canvasToBareBase64(c, 0.1);
      if (b64) best = b64;
      if (b64 && b64.length <= THUMB_B64_CAP) return b64;
    } catch {
      break;
    }
  }
  return best;
}

/**
 * Prepare one image for sending.
 * HD keeps the ORIGINAL bytes for web-safe formats — re-encoding a JPEG through a
 * canvas is a generation loss for no gain, and it is exactly the "less quality than
 * the photo I took" complaint the phone side already fixed by passing bytes through.
 * @returns {{ bytes, width, height, thumbB64, quality, mime }}
 */
export async function prepareImage(input, { hd = true } = {}) {
  /* 🟢 CONVERTED FIRST, AND ONLY IF IT HAS TO BE. `normalizeImageFile` returns
     the original untouched for everything the browser can already draw. */
  const file = await normalizeImageFile(input);
  if (file.size > MAX_IMAGE_BYTES) throw new Error(`"${file.name}" is too large (max 25 MB).`);

  let dec;
  try {
    dec = await decodeImage(file);
  } catch {
    /* 🟥 THE MESSAGE NO LONGER TELLS THEM TO GO AND CONVERT IT — that advice is
       what this file now does for them, so reaching here means something else
       went wrong and saying "export it as JPEG" would send them off to fix a
       problem they do not have. */
    throw new Error(`This browser could not read "${file.name}".`);
  }

  const { drawable, width, height, release } = dec;
  try {
    const thumbB64 = makeThumbBase64(drawable, width, height);
    const safe = WEB_SAFE.includes(file.type);

    if (hd && safe) {
      // untouched original — full quality, identical to what the phone does for HD
      return { bytes: await readFileBytes(file), width, height, thumbB64, quality: 'hd', mime: file.type };
    }

    // SD (or an exotic format): downscale the long edge and re-encode as JPEG.
    const maxEdge = hd ? Math.max(width, height) : 1600;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const canvas = drawToCanvas(drawable, width * scale, height * scale);
    const bytes = await canvasToBytes(canvas, 'image/jpeg', hd ? 0.95 : 0.72);
    return {
      bytes,
      width: canvas.width,
      height: canvas.height,
      thumbB64,
      quality: hd ? 'hd' : 'sd',
      mime: 'image/jpeg',
    };
  } finally {
    try { release(); } catch {}
  }
}

/**
 * Prepare one video: read the bytes as-is (no browser-side transcode — that would
 * need ffmpeg.wasm and cost minutes) and grab a real first-frame poster so the
 * phone has something to paint immediately.
 * @returns {{ bytes, width, height, thumbB64, durationMs, quality, mime }}
 */
export async function prepareVideo(file) {
  if (file.size > MAX_VIDEO_BYTES) throw new Error(`"${file.name}" is too large (max 300 MB).`);

  const url = URL.createObjectURL(file);
  let width = 0, height = 0, durationMs = 0, thumbB64 = null;
  try {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.src = url;

    await new Promise((resolve, reject) => {
      const done = () => resolve();
      v.onloadedmetadata = done;
      v.onerror = () => reject(new Error('metadata failed'));
      setTimeout(done, 6000); // never hang the send on a codec the browser dislikes
    }).catch(() => {});

    width = v.videoWidth || 0;
    height = v.videoHeight || 0;
    durationMs = Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : 0;

    // seek a hair into the clip — frame 0 of many recordings is black
    if (width && height) {
      await new Promise((resolve) => {
        const grab = () => resolve();
        v.onseeked = grab;
        try { v.currentTime = Math.min(0.15, (v.duration || 1) / 4); } catch { grab(); }
        setTimeout(grab, 3000);
      });
      try { thumbB64 = makeThumbBase64(v, width, height); } catch {}
    }
  } finally {
    try { URL.revokeObjectURL(url); } catch {}
  }

  return {
    bytes: await readFileBytes(file),
    width, height, thumbB64, durationMs,
    quality: 'hd',
    mime: file.type || 'video/mp4',
  };
}

/** One entry point the composer uses for whatever got dropped/picked/pasted. */
export async function prepareFile(file) {
  if (isVideoFile(file)) return { kind: 'video', ...(await prepareVideo(file)) };
  if (isImageFile(file)) return { kind: 'image', ...(await prepareImage(file)) };
  throw new Error(`"${file.name}" isn't a photo or video.`);
}

/** Local preview URL for the compose tray + the optimistic bubble (no decrypt round-trip). */
export function localPreviewUrl(file) {
  try { return URL.createObjectURL(file); } catch { return null; }
}

export function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function durationLabel(ms) {
  if (!ms || !Number.isFinite(ms)) return '';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
